const PROFILE_PICTURE_SAS_REFRESH_BUFFER_MS = 60 * 1000;
const MAX_PROFILE_PICTURE_SAS_USER_IDS = 200;

type CachedProfilePicture = {
  url: string;
  expiresAtMs: number;
};

type ProfilePictureSasResponse = {
  profilePictures: Array<{
    userId: string;
    url: string;
    expiresAt: string;
  }>;
};

const cache = new Map<string, CachedProfilePicture>();
const listeners = new Map<string, Set<(url: string | undefined) => void>>();
const pendingUserIds = new Set<string>();

let flushTimer: ReturnType<typeof setTimeout> | null = null;

const isFresh = (cached: CachedProfilePicture | undefined): cached is CachedProfilePicture =>
  cached != null && cached.expiresAtMs - Date.now() > PROFILE_PICTURE_SAS_REFRESH_BUFFER_MS;

const notify = (userId: string, url: string | undefined) => {
  listeners.get(userId)?.forEach((listener) => listener(url));
};

const getCachedUrl = (userId: string): string | undefined => {
  const cached = cache.get(userId);
  return isFresh(cached) ? cached.url : undefined;
};

const chunkUserIds = (userIds: string[]): string[][] => {
  const chunks: string[][] = [];
  for (let i = 0; i < userIds.length; i += MAX_PROFILE_PICTURE_SAS_USER_IDS) {
    chunks.push(userIds.slice(i, i + MAX_PROFILE_PICTURE_SAS_USER_IDS));
  }
  return chunks;
};

const fetchProfilePictureSasChunk = async (userIds: string[]) => {
  try {
    const response = await fetch("/api/trpc/users/profile-picture-sas", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      userIds.forEach((userId) => notify(userId, undefined));
      return;
    }

    const data = (await response.json()) as ProfilePictureSasResponse;
    const resolvedUserIds = new Set<string>();

    data.profilePictures.forEach((profilePicture) => {
      const cached = {
        url: profilePicture.url,
        expiresAtMs: new Date(profilePicture.expiresAt).getTime(),
      };
      cache.set(profilePicture.userId, cached);
      resolvedUserIds.add(profilePicture.userId);
      notify(profilePicture.userId, cached.url);
    });

    userIds.forEach((userId) => {
      if (!resolvedUserIds.has(userId)) {
        notify(userId, undefined);
      }
    });
  } catch {
    userIds.forEach((userId) => notify(userId, undefined));
  }
};

const flushPending = async () => {
  flushTimer = null;

  const userIds = [...pendingUserIds].filter((userId) => !isFresh(cache.get(userId)));
  pendingUserIds.clear();

  if (userIds.length === 0) return;

  await Promise.all(chunkUserIds(userIds).map(fetchProfilePictureSasChunk));
};

export const subscribeProfilePictureSasUrl = (
  userId: string,
  listener: (url: string | undefined) => void,
): (() => void) => {
  const cachedUrl = getCachedUrl(userId);
  if (cachedUrl) {
    listener(cachedUrl);
    return () => {};
  }

  const userListeners = listeners.get(userId) ?? new Set<(url: string | undefined) => void>();
  userListeners.add(listener);
  listeners.set(userId, userListeners);

  pendingUserIds.add(userId);
  if (flushTimer == null) {
    flushTimer = setTimeout(flushPending, 0);
  }

  return () => {
    userListeners.delete(listener);
    if (userListeners.size === 0) {
      listeners.delete(userId);
    }
  };
};
